import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const EMPTY = {
  name: "", country: "Pakistan", contact_person: "",
  contact_channel: "whatsapp", contact_value: "", email: "",
  commission_pct: 0, status: "active", notes: "",
};

export default function Brokers() {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/brokers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const canManage = hasRole("admin", "accounts", "operations");

  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (b) => { setEditing(b); setForm({ ...EMPTY, ...b }); setOpen(true); };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, commission_pct: Number(form.commission_pct) || 0 };
      if (editing) await api.put(`/brokers/${editing.id}`, payload);
      else await api.post("/brokers", payload);
      toast.success(editing ? "Broker updated" : "Broker created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const filtered = items.filter((b) =>
    b.name?.toLowerCase().includes(q.toLowerCase()) ||
    b.code?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="brokers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Intermediaries</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Brokers</h1>
          <p className="text-xs text-zinc-500 mt-1">People who hold your cash and pay suppliers on your behalf.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="broker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-9 pl-8 w-72 rounded-sm" />
          </div>
          {canManage && (
            <Button onClick={startNew} className="h-9 rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="add-broker-btn">
              <Plus size={14} className="mr-1" /> New Broker
            </Button>
          )}
        </div>
      </div>

      <div className="card-sharp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="label-mini px-4 py-3">Code</th>
              <th className="label-mini px-4 py-3">Name</th>
              <th className="label-mini px-4 py-3">Contact</th>
              <th className="label-mini px-4 py-3">Commission %</th>
              <th className="label-mini px-4 py-3 text-right">Holding of ours (PKR)</th>
              <th className="label-mini px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`broker-row-${b.code}`}>
                <td className="px-4 py-3 font-mono text-xs">{b.code}</td>
                <td className="px-4 py-3 font-medium">{b.name}
                  {b.contact_person && <div className="text-[11px] text-zinc-500">{b.contact_person}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{b.contact_channel}: {b.contact_value || "—"}</td>
                <td className="px-4 py-3 text-xs">{b.commission_pct}%</td>
                <td className="px-4 py-3 text-right font-mono tnum">{fmtMoney(b.balance || 0, "")}</td>
                <td className="px-4 py-3 text-right">
                  {canManage && <button onClick={() => startEdit(b)} className="text-xs font-semibold underline mr-3">Edit</button>}
                  <Link to={`/brokers/${b.id}`} className="text-xs font-semibold underline">View</Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-zinc-500 text-sm">No brokers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Broker" : "New Broker"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Field label="Name" required>
                <Input data-testid="brk-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9" />
              </Field>
            </div>
            <Field label="Contact Person">
              <Input value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Channel">
              <Select value={form.contact_channel} onValueChange={(v) => setForm({ ...form, contact_channel: v })}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="wechat">WeChat</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Contact Value">
              <Input value={form.contact_value || ""} onChange={(e) => setForm({ ...form, contact_value: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Email">
              <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Commission %">
              <Input type="number" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} className="rounded-sm h-9 font-mono" />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="col-span-2">
              <Field label="Notes">
                <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-zinc-200 rounded-sm p-2 text-sm h-20" />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="save-broker-btn">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="label-mini">{label}{required && <span className="text-red-600">*</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
